import { ReactNode } from 'react';
import styled from '@emotion/styled';
import { ToggleCard } from './ToggleCard';
import { PlanCard } from './PlanCard';
import type { PlanItem } from '@/types/api';
import { formatCurrency } from '@/utils/currency';

const PlansWrapper = styled.section`
  display: grid;
  gap: 14px;
`;

const frequencyLabels: Record<string, string> = {
  weekly: 'week',
  monthly: 'month',
  yearly: 'year',
};

interface PlansSectionProps {
  plans: PlanItem[];
  selectedPlanId: string | null;
  onSelect: (planId: string) => void;
  toggle?: ReactNode;
  children?: ReactNode;
}

export function PlansSection({
  plans,
  selectedPlanId,
  onSelect,
  toggle,
  children,
}: PlansSectionProps) {
  if (!plans.length) {
    return null;
  }

  const hasToggleContent = Boolean(children ?? toggle);

  return (
    <PlansWrapper>
      {hasToggleContent && (
        <ToggleCard checked>
          {children || toggle}
        </ToggleCard>
      )}

      {plans.map((plan) => {
        if (!plan.trial) {
          return null;
        }

        const trialDays = plan.trial.periodDays;
        const recurring = formatCurrency(plan.price, plan.currency);
        const frequencyLabel = frequencyLabels[plan.billingFrequency] ?? plan.billingFrequency;

        return (
          <PlanCard
            key={plan.planId}
            name={`${trialDays}\u2011Day Trial`}
            price={`then ${recurring} / ${frequencyLabel}`}
            selected={plan.planId === selectedPlanId}
            onSelect={() => onSelect(plan.planId)}
          />
        );
      })}
    </PlansWrapper>
  );
}
